import { useToast } from 'lib/toast'

import * as Sentry from '@sentry/react'

export class ResponseError extends Error {
  status: number
  messages: string | string[]
  constructor(status: number, messages: string | string[]) {
    if (typeof messages === 'string') {
      super(messages)
    } else {
      super(messages[0])
    }
    this.status = status
    this.messages = messages
  }
}
export class ForbiddenError extends ResponseError {
  constructor(status: number, messages: string) {
    super(status, messages)
  }
}

export class ValidationError extends Error {
  constructor(message: string) {
    super(message)
  }
}

export const isResponseError = (error: unknown): error is ResponseError => {
  if (error === undefined || error === null) {
    return false
  }
  const valueHasMessagesProperty =
    error instanceof Object &&
    'messages' in error &&
    (Array.isArray((<{ messages: unknown }>error).messages) ||
      typeof (<{ messages: unknown }>error).messages === 'string')
  const valueHasStatusProperty =
    error instanceof Object &&
    'status' in error &&
    typeof (<{ status: unknown }>error).status === 'number'
  if (valueHasMessagesProperty && valueHasStatusProperty) {
    return true
  }
  return false
}

type ErrorHandler = (error: any) => void

export const generateErrorHandler = (
  handler: (status: number | undefined, message: string | string[]) => void,
  defaultErrorMessage?: string,
): ErrorHandler => {
  return (error: any) => {
    if (isResponseError(error)) {
      handler(error.status, error.messages)
    } else {
      if (error instanceof Error) {
        Sentry.captureException(error)
      } else {
        Sentry.captureMessage(error)
      }
      handler(
        undefined,
        defaultErrorMessage ? defaultErrorMessage : '通信エラーが発生しました',
      )
    }
  }
}

/**
 * APIのレスポンスのエラーハンドリングをするフック
 *
 * 使用例)
 * const errorHander = useErrorHandler()
 *
 * somePromise.catch(errorHander.default)
 * somePromise.catch(errorHander.customText('スカウトを送信できませんでした')
 * somePromise.catch(errorHander.generate((status, message) => {
 *   window.alert(message)
 * }, 'スカウトを送信できませんでした'))
 * somePromise.catch(errorHander.silent)
 * somePromise.catch(errorHander.sentryOnly)
 */
export const useErrorHandler = () => {
  const toast = useToast()
  const showError = (
    status: number | undefined,
    message: string | string[],
  ) => {
    toast({
      id: status == 401 ? 'sessionExpiredToast' : undefined,
      title: status === 422 ? 'Validation Error' : 'Error',
      description: message,
      status: 'error',
    })
  }
  return {
    default: generateErrorHandler(showError),
    customText: generateErrorHandler.bind(this, showError),
    generate: generateErrorHandler,
    silent: (_error: any) => {
      /* catchはしたいが何もしたくない時用に一応用意した */
    },
    sentryOnly: generateErrorHandler(() => {}),
  }
}
